import type { ForecastingRow } from '@/api/forecasting';
import type { ChannelSlice } from '../forecasting.types';
import type { ChannelMarginRow, MarginWaterfallTotals, MonthlyMarginRow, WaterfallChartRow } from '../margin.types';
import { isDynamicForecastStage } from './dynamicForecastMetrics';
import { isBookedStatus } from './monthlyStageRules';
import { getDrillLevelConfig } from './drilldownTable';
import {
  monthKeyFromDate,
  monthSequenceFromRange,
  parseRowMonthDate
} from './forecastingAggregations';
import { parseMetricNumber } from './forecastingNumbers';
import {
  bookedRevenue,
  pipeRevenue,
  prePipeRevenue,
  rowWeight
} from './marginRowMetrics';

function marginPct(revenue: number, gp: number): number {
  return revenue > 0 ? (gp / revenue) * 100 : 0;
}

function dimensionValue(row: ForecastingRow, field: keyof ForecastingRow): string {
  const raw = row[field];
  if (raw == null || raw === '') return 'Unknown';
  return String(raw).trim() || 'Unknown';
}

function aggregateMarginMetrics(rows: ForecastingRow[], field: keyof ForecastingRow): Map<string, {
  booked: number;
  pipe: number;
  prePipe: number;
  hardCosts: number;
  rebate: number;
  gp: number;
  revenue: number;
}> {
  const map = new Map<
    string,
    {
      booked: number;
      pipe: number;
      prePipe: number;
      hardCosts: number;
      rebate: number;
      gp: number;
      revenue: number;
    }
  >();

  for (const row of rows) {
    /* Booked counts in any month; everything else here is the dynamic
       forecast's, so a booked row outside it adds to Booked alone. */
    const isDynamic = isDynamicForecastStage(row.Forecast_Stage);
    const booked = bookedRevenue(row);
    if (!isDynamic && !booked) continue;
    const dim = dimensionValue(row, field);
    const cur = map.get(dim) ?? {
      booked: 0,
      pipe: 0,
      prePipe: 0,
      hardCosts: 0,
      rebate: 0,
      gp: 0,
      revenue: 0
    };

    cur.booked += booked;
    if (!isDynamic) {
      map.set(dim, cur);
      continue;
    }

    const w = rowWeight(row);
    cur.pipe += pipeRevenue(row);
    cur.prePipe += prePipeRevenue(row);
    cur.hardCosts += parseMetricNumber(row.Hard_Costs) * w;
    cur.rebate += parseMetricNumber(row.Rebate) * w;
    cur.gp += parseMetricNumber(row.Gross_Profit) * w;
    cur.revenue += parseMetricNumber(row.Net_Rev__Rec_) * w;
    map.set(dim, cur);
  }

  return map;
}

function mapToMarginRows(
  map: Map<string, {
    booked: number;
    pipe: number;
    prePipe: number;
    hardCosts: number;
    rebate: number;
    gp: number;
    revenue: number;
  }>,
  keyPrefix: string
): ChannelMarginRow[] {
  return [...map.entries()]
    .map(([label, v]) => {
      const bookedPlusPipe = v.booked + v.pipe;
      const rev = v.revenue > 0 ? v.revenue : bookedPlusPipe + v.prePipe;
      return {
        key: `${keyPrefix}:${label}`,
        label,
        booked: v.booked,
        pipe: v.pipe,
        bookedPlusPipe,
        prePipe: v.prePipe,
        hardCosts: v.hardCosts,
        rebate: v.rebate,
        gp: v.gp,
        margin: marginPct(rev, v.gp)
      };
    })
    .sort((a, b) => b.bookedPlusPipe - a.bookedPlusPipe);
}

export function groupMarginDrillSummary(rows: ForecastingRow[], levelIndex: number): ChannelMarginRow[] {
  const level = getDrillLevelConfig(levelIndex);
  const map = aggregateMarginMetrics(rows, level.field);
  return mapToMarginRows(map, level.level);
}

export function groupChannelMargins(rows: ForecastingRow[]): ChannelMarginRow[] {
  const channelLevelIndex = 2;
  return groupMarginDrillSummary(rows, channelLevelIndex);
}

export function computeMarginWaterfallTotals(rows: ForecastingRow[]): MarginWaterfallTotals {
  let bookedPlusPipe = 0;
  let revenue = 0;
  let agencyFees = 0;
  let mediaCost = 0;
  let dspFees = 0;
  let tradingDeals = 0;
  let grossProfit = 0;

  for (const row of rows) {
    // Booked counts in any month; the rest is the dynamic forecast's.
    if (!isDynamicForecastStage(row.Forecast_Stage)) {
      bookedPlusPipe += bookedRevenue(row);
      continue;
    }
    const w = rowWeight(row);
    revenue += parseMetricNumber(row.Net_Rev__Rec_) * w;
    agencyFees += parseMetricNumber(row.Referral) * w;
    mediaCost += parseMetricNumber(row.Platform_Spend) * w;
    dspFees += parseMetricNumber(row.Telco_Costs) * w;
    tradingDeals += parseMetricNumber(row.Rebate) * w;
    grossProfit += parseMetricNumber(row.Gross_Profit) * w;
    bookedPlusPipe += bookedRevenue(row) + pipeRevenue(row);
  }

  return {
    bookedPlusPipe,
    revenue,
    agencyFees,
    mediaCost,
    dspFees,
    tradingDeals,
    grossProfit,
    marginPct: marginPct(revenue, grossProfit)
  };
}

export function buildWaterfallChartData(totals: MarginWaterfallTotals): WaterfallChartRow[] {
  const steps: WaterfallChartRow[] = [
    {
      key: 'revenue',
      label: 'Revenue',
      helper: 0,
      amount: totals.revenue,
      displayValue: totals.revenue,
      kind: 'start'
    }
  ];

  let runningTop = totals.revenue;
  const addDecrease = (key: string, label: string, value: number) => {
    if (value <= 0) return;
    runningTop -= value;
    steps.push({
      key,
      label,
      helper: runningTop,
      amount: value,
      displayValue: value,
      kind: 'decrease'
    });
  };

  addDecrease('agencyFees', 'Agency Fees', totals.agencyFees);
  addDecrease('mediaCost', 'Media Cost', totals.mediaCost);
  addDecrease('dspFees', 'DSP Fees', totals.dspFees);
  addDecrease('tradingDeals', 'Trading Deals', totals.tradingDeals);

  steps.push({
    key: 'grossProfit',
    label: 'Gross Profit',
    helper: 0,
    amount: totals.grossProfit,
    displayValue: totals.grossProfit,
    kind: 'end'
  });

  steps.push({
    key: 'marginPct',
    label: 'Margin %',
    helper: 0,
    amount: 0,
    displayValue: totals.marginPct,
    kind: 'end'
  });

  return steps;
}

export function groupMarginPieByBookedPlusPipe(rows: ForecastingRow[], levelIndex: number): ChannelSlice[] {
  const slices = groupMarginDrillSummary(rows, levelIndex);
  const total = slices.reduce((sum, row) => sum + row.bookedPlusPipe, 0);

  return slices
    .filter((row) => row.bookedPlusPipe > 0)
    .map((row) => ({
      name: row.label,
      value: row.bookedPlusPipe,
      percent: total > 0 ? (row.bookedPlusPipe / total) * 100 : 0
    }));
}

export function groupChannelPieByBookedPlusPipe(rows: ForecastingRow[]): ChannelSlice[] {
  return groupMarginPieByBookedPlusPipe(rows, 2);
}

export function groupMonthlyMargin(
  rows: ForecastingRow[],
  dateFrom: string,
  dateTo: string
): MonthlyMarginRow[] {
  const seq = monthSequenceFromRange(dateFrom, dateTo);
  const map = new Map<string, { bookedPlusPipe: number; gp: number; revenue: number }>();

  for (const { key } of seq) {
    map.set(key, { bookedPlusPipe: 0, gp: 0, revenue: 0 });
  }

  for (const row of rows) {
    const isDynamic = isDynamicForecastStage(row.Forecast_Stage);
    // Booked counts in any month; the rest is the dynamic forecast's.
    if (!isDynamic && !isBookedStatus(row.Status)) continue;
    const md = parseRowMonthDate(row);
    if (!md) continue;
    const key = monthKeyFromDate(md);
    const bucket = map.get(key);
    if (!bucket) continue;

    if (!isDynamic) {
      bucket.bookedPlusPipe += bookedRevenue(row);
      continue;
    }

    const w = rowWeight(row);
    bucket.bookedPlusPipe += bookedRevenue(row) + pipeRevenue(row);
    bucket.gp += parseMetricNumber(row.Gross_Profit) * w;
    bucket.revenue += parseMetricNumber(row.Net_Rev__Rec_) * w;
  }

  return seq.map(({ key, axisLabel }) => {
    const v = map.get(key)!;
    const revenue = v.revenue > 0 ? v.revenue : v.bookedPlusPipe;
    return {
      monthKey: key,
      axisLabel,
      bookedPlusPipe: v.bookedPlusPipe,
      gp: v.gp,
      marginPct: marginPct(revenue, v.gp)
    };
  });
}
